import React, { useState, useEffect, useCallback } from 'react';
import { TableSkeleton } from '../../components/enterprise';
import {
    Box, Typography, Paper, Button, Chip, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Alert, CircularProgress,
    IconButton, Tooltip, useTheme, Switch, Select, MenuItem,
    FormControl, InputLabel, FormControlLabel, InputAdornment,
    Stack, Card, CardContent, Grid, alpha,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Add as AddIcon, Delete as DeleteIcon, Refresh as RefreshIcon,
    ContentCopy as CopyIcon, Autorenew as RotateIcon,
    Edit as EditIcon, Cloud as CloudIcon,
    Visibility as ViewIcon, VisibilityOff as HideIcon,
    CloudQueue as CloudQueueIcon, Public as PublicIcon,
    Share as ShareIcon, LocationOn as LocationIcon,
} from '@mui/icons-material';
import billingService from '../../services/billingService';
import { useNotification } from '../../contexts/NotificationContext';
import ConfirmDialog from '../../components/enterprise/ConfirmDialog';
import { parseServerTs, toServerDate } from '../../timezone';

const REGIONS = [
    { value: 'us-east-1', label: 'US East (N. Virginia)' },
    { value: 'us-west-2', label: 'US West (Oregon)' },
    { value: 'eu-west-1', label: 'Europe (Ireland)' },
    { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
    { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
    { value: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
    { value: 'sa-east-1', label: 'South America (Sao Paulo)' },
    { value: 'custom', label: 'Custom Location' },
];

const CLOUD_PROVIDERS = [
    { value: 'AWS', label: 'AWS' },
    { value: 'GCP', label: 'GCP' },
    { value: 'Azure', label: 'Azure' },
    { value: 'DigitalOcean', label: 'DigitalOcean' },
    { value: 'Custom', label: 'Custom' },
];

const ENVIRONMENTS = [
    { value: 'production', label: 'Production' },
    { value: 'staging', label: 'Staging' },
    { value: 'development', label: 'Development' },
    { value: 'testing', label: 'Testing' },
];

const PROVIDER_COLORS = {
    AWS: '#FF9900',
    GCP: '#4285F4',
    Azure: '#0078D4',
    DigitalOcean: '#0080FF',
    Custom: '#6B7280',
};

const formatRelative = (dt) => {
    const ts = parseServerTs(dt);
    if (!Number.isFinite(ts)) return 'Never';
    const diff = Date.now() - ts;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return `${Math.floor(diff / 86400000)}d ago`;
};

const formatDate = (dt) => toServerDate(dt)?.toLocaleString() ?? '-';

const emptyFormState = {
    name: '',
    cloudProvider: 'AWS',
    cloudLocation: '',
    region: '',
    environment: 'production',
    shared: false,
};

const SyntheticCloudAgentsPage = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { showNotification } = useNotification();

    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);

    // Create/Edit dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [formData, setFormData] = useState({ ...emptyFormState });
    const [saving, setSaving] = useState(false);

    // Connection key dialog
    const [keyDialog, setKeyDialog] = useState({ open: false, key: '', agentName: '' });
    const [keyVisible, setKeyVisible] = useState(false);
    const [copied, setCopied] = useState(false);

    // Rotate key confirmation
    const [rotateDialog, setRotateDialog] = useState({ open: false, id: null, name: '' });

    // Delete confirmation
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });

    const fetchAgents = useCallback(async () => {
        try {
            setLoading(true);
            const res = await billingService.cloudAgents.list();
            setAgents(res.data || []);
        } catch (err) {
            showNotification('Failed to load cloud agents: ' + (err.response?.data?.error || err.message), 'error');
        } finally {
            setLoading(false);
        }
    }, [showNotification]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    const handleOpenCreate = () => {
        setEditingAgent(null);
        setFormData({ ...emptyFormState });
        setDialogOpen(true);
    };

    const handleOpenEdit = (agent) => {
        setEditingAgent(agent);
        setFormData({
            name: agent.name || '',
            cloudProvider: agent.cloudProvider || 'AWS',
            cloudLocation: agent.cloudLocation || '',
            region: agent.region || '',
            environment: agent.environment || 'production',
            shared: agent.shared || false,
        });
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditingAgent(null);
        setFormData({ ...emptyFormState });
    };

    const handleSave = async () => {
        if (!formData.name.trim()) return;
        try {
            setSaving(true);
            if (editingAgent) {
                await billingService.cloudAgents.update(editingAgent.id, formData);
                showNotification(`Cloud agent "${formData.name}" updated successfully`, 'success');
                handleCloseDialog();
                fetchAgents();
            } else {
                const res = await billingService.cloudAgents.create(formData);
                handleCloseDialog();
                if (res.data?.connectionKey) {
                    setKeyDialog({
                        open: true,
                        key: res.data.connectionKey,
                        agentName: res.data.name || formData.name,
                    });
                    setKeyVisible(false);
                    setCopied(false);
                }
                showNotification(`Cloud agent "${formData.name}" registered successfully`, 'success');
                fetchAgents();
            }
        } catch (err) {
            showNotification(
                (editingAgent ? 'Update' : 'Registration') + ' failed: ' + (err.response?.data?.error || err.message),
                'error'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleRotateKey = async () => {
        try {
            const res = await billingService.cloudAgents.rotateKey(rotateDialog.id);
            setRotateDialog({ open: false, id: null, name: '' });
            setKeyDialog({
                open: true,
                key: res.data?.connectionKey || res.data?.key || '',
                agentName: res.data?.name || rotateDialog.name,
            });
            setKeyVisible(false);
            setCopied(false);
            showNotification('Connection key rotated successfully', 'success');
            fetchAgents();
        } catch (err) {
            showNotification('Key rotation failed: ' + (err.response?.data?.error || err.message), 'error');
        }
    };

    const handleDelete = async () => {
        try {
            await billingService.cloudAgents.delete(deleteDialog.id);
            setDeleteDialog({ open: false, id: null, name: '' });
            showNotification('Cloud agent deleted', 'success');
            fetchAgents();
        } catch (err) {
            showNotification('Delete failed: ' + (err.response?.data?.error || err.message), 'error');
        }
    };

    const copyKey = () => {
        navigator.clipboard.writeText(keyDialog.key);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleFormChange = (field) => (e) => {
        const value = field === 'shared' ? e.target.checked : e.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    // Stats
    const totalAgents = agents.length;
    const onlineAgents = agents.filter((a) => a.status === 'ONLINE').length;
    const sharedAgents = agents.filter((a) => a.shared).length;
    const uniqueRegions = new Set(agents.map((a) => a.region).filter(Boolean)).size;

    const statsCards = [
        {
            label: 'Total Cloud Agents',
            value: totalAgents,
            icon: <CloudQueueIcon />,
            color: theme.palette.primary.main,
        },
        {
            label: 'Online',
            value: onlineAgents,
            icon: <CloudIcon />,
            color: theme.palette.success.main,
        },
        {
            label: 'Shared',
            value: sharedAgents,
            icon: <ShareIcon />,
            color: theme.palette.info.main,
        },
        {
            label: 'Regions',
            value: uniqueRegions,
            icon: <LocationIcon />,
            color: theme.palette.warning.main,
        },
    ];

    const columns = [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            minWidth: 160,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight={600}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'agentId',
            headerName: 'Agent ID',
            flex: 1,
            minWidth: 180,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 110,
            renderCell: (params) => {
                const status = params.value || 'OFFLINE';
                const colorMap = { ONLINE: 'success', OFFLINE: 'error', BUSY: 'warning' };
                return (
                    <Chip
                        label={status}
                        color={colorMap[status] || 'default'}
                        size="small"
                        variant="outlined"
                    />
                );
            },
        },
        {
            field: 'cloudProvider',
            headerName: 'Cloud Provider',
            width: 140,
            renderCell: (params) => {
                const provider = params.value || 'Custom';
                const providerColor = PROVIDER_COLORS[provider] || PROVIDER_COLORS.Custom;
                return (
                    <Chip
                        label={provider}
                        size="small"
                        sx={{
                            bgcolor: alpha(providerColor, 0.12),
                            color: providerColor,
                            fontWeight: 600,
                            border: `1px solid ${alpha(providerColor, 0.3)}`,
                        }}
                    />
                );
            },
        },
        {
            field: 'cloudLocation',
            headerName: 'Location',
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary">
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'shared',
            headerName: 'Shared',
            width: 100,
            renderCell: (params) => (
                <Chip
                    label={params.value ? 'Shared' : 'Private'}
                    size="small"
                    variant="outlined"
                    color={params.value ? 'success' : 'default'}
                />
            ),
        },
        {
            field: 'region',
            headerName: 'Region',
            width: 130,
            renderCell: (params) => {
                const regionMeta = REGIONS.find((r) => r.value === params.value);
                return (
                    <Typography variant="body2" color="text.secondary">
                        {regionMeta?.label || params.value || '-'}
                    </Typography>
                );
            },
        },
        {
            field: 'environment',
            headerName: 'Environment',
            width: 120,
            renderCell: (params) => (
                <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                    {params.value || '-'}
                </Typography>
            ),
        },
        {
            field: 'lastHeartbeat',
            headerName: 'Last Heartbeat',
            width: 140,
            renderCell: (params) => (
                <Tooltip title={formatDate(params.value)}>
                    <Typography variant="body2" color={params.value ? 'text.primary' : 'text.disabled'}>
                        {formatRelative(params.value)}
                    </Typography>
                </Tooltip>
            ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 140,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Rotate Key">
                        <IconButton
                            size="small"
                            onClick={() => setRotateDialog({ open: true, id: params.row.id, name: params.row.name })}
                        >
                            <RotateIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteDialog({ open: true, id: params.row.id, name: params.row.name })}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ p: 3, maxWidth: 1400 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>Cloud Agents</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Register and manage cloud-provided synthetic agents for distributed test execution.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button startIcon={<RefreshIcon />} onClick={fetchAgents} disabled={loading}>
                        Refresh
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                        Register Cloud Agent
                    </Button>
                </Box>
            </Box>

            {/* Stats Row */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                {statsCards.map((stat) => (
                    <Grid item xs={12} sm={6} md={3} key={stat.label}>
                        <Card
                            variant="outlined"
                            sx={{
                                bgcolor: isDark ? alpha(stat.color, 0.06) : alpha(stat.color, 0.04),
                                borderColor: alpha(stat.color, 0.2),
                            }}
                        >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, '&:last-child': { pb: 2 } }}>
                                <Box
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: alpha(stat.color, 0.12),
                                        color: stat.color,
                                    }}
                                >
                                    {stat.icon}
                                </Box>
                                <Box>
                                    <Typography variant="h5" fontWeight={700}>{stat.value}</Typography>
                                    <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* DataGrid */}
            <Paper sx={{ bgcolor: isDark ? '#0d1117' : '#fff' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <TableSkeleton rows={5} columns={4} />
                    </Box>
                ) : agents.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <CloudQueueIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                        <Typography color="text.secondary" sx={{ mb: 0.5 }}>No cloud agents registered</Typography>
                        <Typography variant="body2" color="text.disabled">
                            Register a cloud agent to enable distributed synthetic testing across regions.
                        </Typography>
                    </Box>
                ) : (
                    <DataGrid
                        rows={agents}
                        columns={columns}
                        getRowId={(row) => row.id || row.agentId}
                        autoHeight
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } },
                        }}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-cell': {
                                display: 'flex',
                                alignItems: 'center',
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: isDark ? alpha('#fff', 0.03) : alpha('#000', 0.02),
                            },
                        }}
                    />
                )}
            </Paper>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editingAgent ? 'Edit Cloud Agent' : 'Register Cloud Agent'}</DialogTitle>
                <DialogContent>
                    {!editingAgent && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            A unique connection key will be generated. You must copy it immediately — it will not be shown again.
                        </Alert>
                    )}
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth
                            label="Agent Name"
                            required
                            value={formData.name}
                            onChange={handleFormChange('name')}
                            placeholder="e.g. cloud-agent-us-east-01"
                        />
                        <FormControl fullWidth>
                            <InputLabel>Cloud Provider</InputLabel>
                            <Select
                                value={formData.cloudProvider}
                                label="Cloud Provider"
                                onChange={handleFormChange('cloudProvider')}
                            >
                                {CLOUD_PROVIDERS.map((p) => (
                                    <MenuItem key={p.value} value={p.value}>
                                        {p.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Cloud Location"
                            value={formData.cloudLocation}
                            onChange={handleFormChange('cloudLocation')}
                            placeholder="e.g. US East (Virginia)"
                        />
                        <FormControl fullWidth>
                            <InputLabel>Region</InputLabel>
                            <Select
                                value={formData.region}
                                label="Region"
                                onChange={handleFormChange('region')}
                            >
                                <MenuItem value="">
                                    <em>Not specified</em>
                                </MenuItem>
                                {REGIONS.map((r) => (
                                    <MenuItem key={r.value} value={r.value}>
                                        {r.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Environment</InputLabel>
                            <Select
                                value={formData.environment}
                                label="Environment"
                                onChange={handleFormChange('environment')}
                            >
                                {ENVIRONMENTS.map((e) => (
                                    <MenuItem key={e.value} value={e.value}>
                                        {e.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.shared}
                                    onChange={handleFormChange('shared')}
                                    color="primary"
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2">Shared Agent</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Make available to all tenants
                                    </Typography>
                                </Box>
                            }
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        disabled={!formData.name.trim() || saving}
                        startIcon={saving ? <CircularProgress size={16} /> : editingAgent ? <EditIcon /> : <AddIcon />}
                    >
                        {editingAgent ? 'Update' : 'Register'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Connection Key Dialog */}
            <Dialog
                open={keyDialog.open}
                onClose={() => setKeyDialog({ open: false, key: '', agentName: '' })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Connection Key — {keyDialog.agentName}</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Copy this connection key now. It will not be shown again. If lost, you must rotate to generate a new one.
                    </Alert>
                    <TextField
                        fullWidth
                        label="Connection Key"
                        value={keyDialog.key}
                        type={keyVisible ? 'text' : 'password'}
                        InputProps={{
                            readOnly: true,
                            sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setKeyVisible(!keyVisible)} size="small">
                                        {keyVisible ? <HideIcon /> : <ViewIcon />}
                                    </IconButton>
                                    <IconButton onClick={copyKey} size="small" color={copied ? 'success' : 'default'}>
                                        <CopyIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    {copied && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                            Copied to clipboard
                        </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Configure the synthetic agent with this key:
                    </Typography>
                    <Paper sx={{ p: 1.5, mt: 1, bgcolor: isDark ? '#161b22' : '#f6f8fa', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                        agent.connection-key={keyVisible ? keyDialog.key : '********************************'}
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={() => setKeyDialog({ open: false, key: '', agentName: '' })}>
                        Done
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Rotate Key Confirmation */}
            <ConfirmDialog
                open={rotateDialog.open}
                onClose={() => setRotateDialog({ open: false, id: null, name: '' })}
                onConfirm={handleRotateKey}
                title="Rotate Connection Key"
                message={`This will invalidate the current connection key for "${rotateDialog.name}". The agent will need to be reconfigured with the new key.`}
                confirmText="Rotate Key"
                confirmColor="warning"
                severity="warning"
            />

            {/* Delete Confirmation */}
            <ConfirmDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, id: null, name: '' })}
                onConfirm={handleDelete}
                title="Delete Cloud Agent"
                message={`Permanently delete cloud agent "${deleteDialog.name}"? This cannot be undone and the agent will immediately stop executing tests.`}
                confirmText="Delete"
                confirmColor="error"
                severity="error"
            />
        </Box>
    );
};

export default SyntheticCloudAgentsPage;
